import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Reveal } from "./Reveal";
import { REALISATIONS, CITY_REALISATIONS, COMPANY } from "@/data/content";

// "Nos réalisations" — before/after photos with fullscreen lightbox gallery.
// citySlug (optional) => use that city's own photo set + personalized captions.
export default function Realisations({ citySlug, cityName }) {
  const cityMode = citySlug && CITY_REALISATIONS.has(citySlug);
  const items = cityMode ? REALISATIONS : REALISATIONS.slice(0, 5);
  const base = cityMode ? `/chantier/${citySlug}` : "/chantier";
  const place = cityName || "Lagny-sur-Marne et ses environs";

  const [index, setIndex] = useState(null); // open lightbox index
  const isOpen = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    () => setIndex((i) => (i === null ? i : (i - 1 + items.length) % items.length)),
    [items.length]
  );
  const next = useCallback(
    () => setIndex((i) => (i === null ? i : (i + 1) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close, prev, next]);

  const current = isOpen ? items[index] : null;

  return (
    <section id="realisations" data-testid="realisations" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-golddark">
              <span className="w-8 h-px bg-brand-gold" /> Nos réalisations
            </span>
            <h2 className="mt-5 font-display font-semibold text-4xl md:text-6xl tracking-tight text-brand-navy leading-[1.05]">
              Avant / après {cityName ? `à ${cityName}` : "nos interventions"}
            </h2>
          </div>
          <p className="text-base text-slate-600 max-w-sm leading-relaxed">
            Des chantiers réels réalisés par nos serruriers à {place}. Cliquez
            sur une photo pour l&apos;agrandir.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((r, i) => (
            <Reveal key={r.slug} delay={(i % 3) * 0.06}>
              <figure className="group h-full rounded-2xl overflow-hidden border border-brand-line bg-white shadow-sm hover:shadow-xl hover:shadow-brand-navy/10 hover:-translate-y-1 transition-[box-shadow,transform] duration-300">
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  data-testid={`realisation-image-${i}`}
                  aria-label={`Agrandir : ${r.title}`}
                  className="relative block w-full overflow-hidden cursor-zoom-in"
                >
                  <img
                    src={`${base}/${r.slug}.jpg`}
                    alt={`${r.title} à ${cityName || "Lagny-sur-Marne"} — serrurier (avant / après)`}
                    loading="lazy"
                    className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <span className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/25 transition-colors duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 grid place-items-center w-12 h-12 rounded-full bg-white/95 text-brand-navy">
                      <Expand className="w-5 h-5" />
                    </span>
                  </span>
                </button>
                <figcaption className="p-6">
                  <h3 className="font-display font-medium text-lg text-brand-navy">
                    {r.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    {r.desc(place)}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}

          {!cityMode && (
            <Reveal delay={0.12}>
              <div className="h-full rounded-2xl bg-brand-navy p-8 flex flex-col justify-center">
                <div className="font-display font-semibold text-2xl md:text-3xl text-white leading-tight">
                  Votre serrure a besoin d&apos;un pro ?
                </div>
                <p className="text-white/70 mt-3">
                  Diagnostic honnête, devis gratuit et intervention soignée,
                  comme sur ces chantiers.
                </p>
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  data-testid="realisations-call-btn"
                  className="mt-6 inline-flex items-center gap-3 bg-brand-gold hover:bg-brand-golddark text-brand-navy px-7 py-4 rounded-full font-semibold text-base transition-colors duration-300 w-fit"
                >
                  <Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}
                </a>
              </div>
            </Reveal>
          )}
        </div>
      </div>

      {/* Fullscreen lightbox */}
      <AnimatePresence>
        {isOpen && current && (
          <motion.div
            data-testid="realisation-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-navy/95 backdrop-blur-sm p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
          >
            <button
              type="button"
              onClick={close}
              data-testid="lightbox-close"
              aria-label="Fermer"
              className="absolute top-5 right-5 grid place-items-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200"
            >
              <X className="w-6 h-6" />
            </button>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev"
              aria-label="Photo précédente"
              className="absolute left-3 sm:left-6 grid place-items-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next"
              aria-label="Photo suivante"
              className="absolute right-3 sm:right-6 grid place-items-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <motion.figure
              key={current.slug}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
            >
              <img
                src={`${base}/${current.slug}.jpg`}
                alt={`${current.title} à ${cityName || "Lagny-sur-Marne"} — avant / après`}
                className="w-full h-auto max-h-[75vh] object-contain rounded-xl shadow-2xl"
              />
              <figcaption className="mt-5 text-center">
                <h3 className="font-display font-semibold text-xl text-white">
                  {current.title}
                </h3>
                <p className="mt-1 text-white/70 text-sm max-w-xl mx-auto">
                  {current.desc(place)}
                </p>
                <span className="mt-3 inline-block text-xs uppercase tracking-[0.2em] text-brand-gold">
                  {index + 1} / {items.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
